"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ActionRoomScene = void 0;
const Scene_1 = require("./Scene");
const pixi_js_1 = require("pixi.js");
const SceneManager_1 = require("../managers/SceneManager");
const Global_1 = require("../Global");
const ui_1 = require("@pixi/ui");
const i18next_1 = __importDefault(require("i18next"));
const blocks_1 = require("../blocks");
const gsap_1 = require("gsap");
const brushleft_svg_1 = __importDefault(require("../../assets/svg/brushleft.svg"));
const brushright_svg_1 = __importDefault(require("../../assets/svg/brushright.svg"));
const brushcenter_svg_1 = __importDefault(require("../../assets/svg/brushcenter.svg"));
const WaitingText_1 = require("../objects/WaitingText");
const interfaces_1 = require("../../../shared/interfaces");
const config_json_1 = __importDefault(require("../../../shared/config.json"));
const lodash_1 = __importDefault(require("lodash"));
const WigglyText_1 = require("../objects/WigglyText");
const settings_1 = require("../config/settings");
const question_svg_1 = __importDefault(require("../../assets/svg/question.svg"));
const question_error_svg_1 = __importDefault(require("../../assets/svg/question_error.svg"));
const question_white_svg_1 = __importDefault(require("../../assets/svg/question_white.svg"));
const AssetManager_1 = __importDefault(require("../managers/AssetManager"));
class ActionRoomScene extends Scene_1.Scene {
    constructor(app, sceneManager, gameManager, _type = interfaces_1.GameType.Battle) {
        super(app, sceneManager, gameManager, (0, Global_1.colorForGameType)(_type));
        this._type = _type;
        this.playerlist = new Array(config_json_1.default.roomsize);
        this.switchcount = 0;
        this.maxswitchcount = 5; // We animate player coming in and out until we reach this
        this.visibilityChange = () => {
            // If we are out of focus, take us back to the homepage, because we aren't paying attention anyway...
            if (document.visibilityState === 'hidden') {
                this.sceneManager.routeTo(SceneManager_1.Scenes.Home, null, true).then();
            }
        };
        this.footerText = this.makeFooterText('typeinfo.' + this.type);
        this.footerText.alpha = 0;
        this.footerText.position.y = this.app.screen.height;
        this.centerText = this.makeCenterText();
        this.brushes = this.makeBrushes();
        this.roomContainer = this.makeRoomContainer();
        this.clear().then(() => {
            // const rmfilter = (room: RoomData) => {
            //     return room && (room.type === GameType.Battle || room.type === GameType.BattleFriends);
            // };
            let curlist = [];
            const toremove = (newset) => {
                const remove = [];
                curlist.forEach((p) => {
                    if (!newset.find((v) => v.pkey === (p === null || p === void 0 ? void 0 : p.pkey))) {
                        remove.push(p);
                        return false;
                    }
                    return true;
                });
                return remove;
            };
            const toadd = (newset) => {
                const add = [];
                (newset || []).forEach((p, i) => {
                    if (!curlist.find((v) => (v === null || v === void 0 ? void 0 : v.pkey) === p.pkey)) {
                        add.push(p);
                    }
                });
                return add;
            };
            // Just get the current state of the room
            this.subs.add(this.gameManager.room$.subscribe({
                next: (room) => __awaiter(this, void 0, void 0, function* () {
                    if (room && (room.type === this.type)) {
                        const newset = lodash_1.default.values(room.players);
                        const remover = toremove(newset);
                        const adder = toadd(newset);
                        curlist = newset;
                        for (let i = 0; i < remover.length; i++) {
                            yield this.switchPlayer(remover[i], false);
                        }
                        for (let i = 0; i < adder.length; i++) {
                            yield this.switchPlayer(adder[i], true);
                        }
                    }
                })
            }));
            this.subs.add(this.gameManager.reconnect$.subscribe((num) => __awaiter(this, void 0, void 0, function* () {
                yield this.clear();
                curlist = [];
                this.joinRoom();
                this.layout();
            })));
        });
    }
    clear() {
        return __awaiter(this, void 0, void 0, function* () {
            for (let i = 0; i < this.playerlist.length; i++) {
                yield this.setPlayer(i, null, false);
            }
        });
    }
    get type() {
        return this._type || interfaces_1.GameType.Battle;
    }
    switchPlayer(player, add = true) {
        this.switchcount++;
        return this.series(() => __awaiter(this, void 0, void 0, function* () {
            var _a;
            const found = lodash_1.default.findIndex(this.playerlist, (p) => p && p.pkey === player.pkey);
            if (add && found === -1) {
                AssetManager_1.default.popSound();
                const nextspot = lodash_1.default.findIndex(this.playerlist, (p) => p === null);
                yield this.setPlayer(nextspot, player, this.switchcount <= this.maxswitchcount);
            }
            else if (!add && found !== -1) {
                yield this.setPlayer(found, null, this.switchcount <= this.maxswitchcount);
            }
            // Check if we are full, if so, we are ready to start the game
            const full = this.playerlist.reduce((p, c) => c ? p + 1 : p, 0) === config_json_1.default.roomsize;
            if (full && ((_a = this.gameManager.room$.value) === null || _a === void 0 ? void 0 : _a.open)) {
                this.gameManager.notifyReady();
            }
            if (--this.switchcount < 0) {
                this.switchcount = 0;
            }
        }));
    }
    setPlayer(atIndex, player = null, animate = true) {
        return __awaiter(this, void 0, void 0, function* () {
            const texter = this.roomContainer.getChildByName('value' + atIndex);
            if (texter) {
                const tindex = this.brushes.length - atIndex - 1;
                if (player) {
                    texter.stop();
                    texter.style.fontSize = (0, Global_1.fontSize)(this.fontsize, player.l);
                    texter.style.fontFamily = (0, Global_1.font)(player.l);
                    texter.text = player.u;
                    texter.position.x = texter.width / 2 * ((0, Global_1.isrtl)() ? -1 : 1);
                    texter.scale.set(0.2);
                    texter.rotation = 1;
                    if (tindex >= 0) {
                        try {
                            yield this.promisedTimeline(gsap_1.gsap.timeline()
                                .to(this.brushes[tindex], {
                                pixi: {
                                    positionX: this.isLandscapeBox ? this.center.x / 2 : this.center.x,
                                    alpha: 1.0
                                },
                                duration: animate ? 0.3 : 0
                            })
                                .to(texter.style, {
                                fill: '#' + settings_1.Settings.blackColor.toString(16).padStart(6, '0'),
                                duration: animate ? 0.5 : 0
                            }, '<')
                                .to(texter, {
                                pixi: {
                                    scale: 1.6,
                                    rotation: 10 * (atIndex % 2 ? 1 : -1)
                                },
                                ease: 'power2.out',
                                duration: animate ? 0.3 : 0
                            }, '<')
                                .to(texter, {
                                pixi: {
                                    scale: 1.0,
                                    rotation: 0
                                },
                                ease: 'power2.in',
                                duration: animate ? 0.3 : 0
                            }, '>'));
                        }
                        catch (e) {
                            console.warn('wat1', e);
                        }
                    }
                }
                else {
                    if (this.playerlist[atIndex]) {
                        texter.scale.set(1.0);
                        if (tindex >= 0) {
                            try {
                                yield this.promisedTimeline(gsap_1.gsap.timeline()
                                    .to(this.brushes[tindex], {
                                    pixi: {
                                        positionX: this.brushXSpot(tindex),
                                        alpha: 0
                                    },
                                    duration: animate ? 0.3 : 0
                                })
                                    .to(texter.style, {
                                    fill: '#' + settings_1.Settings.grayColor.toString(16).padStart(6, '0'),
                                    duration: animate ? 0.5 : 0
                                }, '<')
                                    .to(texter, {
                                    pixi: {
                                        scale: 0.5,
                                        alpha: 0,
                                    },
                                    ease: 'power2.out',
                                    duration: animate ? 0.6 : 0
                                }, '<'));
                            }
                            catch (e) {
                                console.warn('wat2', e);
                            }
                        }
                    }
                    try {
                        // In case we no longer exist...
                        texter.scale.set(1.0);
                        texter.style.fill = settings_1.Settings.grayColor;
                        texter.style.fontSize = (0, Global_1.fontSize)(this.fontsize);
                        texter.style.fontFamily = (0, Global_1.font)();
                        texter.text = this.waitingtext;
                        texter.position.x = texter.width / 2 * ((0, Global_1.isrtl)() ? -1 : 1);
                        texter.start();
                        if (this.playerlist[atIndex]) {
                            yield this.promisedTimeline(gsap_1.gsap.timeline()
                                .to(texter, {
                                pixi: {
                                    alpha: 1.0,
                                },
                                ease: 'power2.in',
                                duration: animate ? 0.5 : 0
                            }, '<'));
                        }
                    }
                    catch (e) {
                        console.warn('wat3', e);
                    }
                }
                this.playerlist[atIndex] = player;
            }
        });
    }
    get waitingtext() {
        return i18next_1.default.t('battle.searching');
    }
    makeRoomContainer() {
        const container = new pixi_js_1.Container();
        const space = this.overSmallSize ? 18 : 12;
        for (let i = 0; i < config_json_1.default.roomsize; i++) {
            const txt = ((0, Global_1.isrtl)() ? ':' + (i + 1) + ' ' : '') + i18next_1.default.t('battle.artist') + (!(0, Global_1.isrtl)() ? ' ' + (i + 1) + ':' : '');
            const labelstyle = blocks_1.Block.textStyle(this.fontsize);
            const y = (t) => i * (t.height + (0, Global_1.px)(space));
            // Label
            const label = new pixi_js_1.Text(txt, labelstyle);
            label.name = 'label' + i;
            label.anchor.set((0, Global_1.isrtl)() ? 0 : 1.08, 0);
            label.position.y = y(label);
            container.addChild(label);
            // Value
            const style = blocks_1.Block.textStyle(this.fontsize);
            style.align = (0, Global_1.isrtl)() ? 'right' : 'left';
            const value = new WaitingText_1.WaitingText(null, style);
            value.name = 'value' + i;
            value.anchor.set(0.5, 0.5);
            value.position.y = y(value) + value.height / 2;
            container.addChild(value);
        }
        return this.addChild(container);
    }
    get fontsize() {
        return this.overSmallSize ? 28 : 24;
    }
    makeCenterText() {
        const container = new pixi_js_1.Container();
        const txt = i18next_1.default.t('battle.findingopponents');
        this.text = new WigglyText_1.WigglyText(this.app, txt);
        this.text.power = (0, Global_1.px)(7);
        this.text.anchor.set(0.5, 0);
        container.addChild(this.text);
        return this.addChild(container);
    }
    makeBrushes() {
        if (this.type === interfaces_1.GameType.Guess || this.type === interfaces_1.GameType.GuessFriends) {
            const brushes = [pixi_js_1.Sprite.from(question_svg_1.default), pixi_js_1.Sprite.from(question_error_svg_1.default), pixi_js_1.Sprite.from(question_white_svg_1.default)];
            for (let i = 0; i < brushes.length; i++) {
                const brushSprite = brushes[i];
                brushSprite.anchor.set(0.5, 0.5);
                if (i == 2) {
                    brushSprite.pivot.set(-brushSprite.width, 0);
                    brushSprite.rotation = .3;
                }
                else if (i == 1) {
                    brushSprite.pivot.set(brushSprite.width, 0);
                    brushSprite.rotation = -.3;
                }
                brushSprite.alpha = 0;
                this.addChild(brushSprite);
            }
            return brushes;
        }
        else {
            const brushes = [pixi_js_1.Sprite.from(brushcenter_svg_1.default), pixi_js_1.Sprite.from(brushleft_svg_1.default), pixi_js_1.Sprite.from(brushright_svg_1.default)];
            for (let i = 0; i < brushes.length; i++) {
                const brushSprite = brushes[i];
                brushSprite.anchor.set(0.5, 0.5);
                brushSprite.alpha = 0;
                this.addChild(brushSprite);
            }
            return brushes;
        }
    }
    animate() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.animateFooterText(this.footerText);
        });
    }
    joinRoom() {
        this.gameManager.enterRoom(this.type);
    }
    init() {
        const _super = Object.create(null, {
            init: { get: () => super.init }
        });
        return __awaiter(this, void 0, void 0, function* () {
            yield _super.init.call(this);
            this.animate().then();
            this.joinRoom();
            document.addEventListener("visibilitychange", this.visibilityChange);
        });
    }
    deinit() {
        const _super = Object.create(null, {
            deinit: { get: () => super.deinit }
        });
        return __awaiter(this, void 0, void 0, function* () {
            yield _super.deinit.call(this);
            document.removeEventListener("visibilitychange", this.visibilityChange);
        });
    }
    exitScene(to, target) {
        const _super = Object.create(null, {
            exitScene: { get: () => super.exitScene }
        });
        return __awaiter(this, void 0, void 0, function* () {
            // Find our button
            const mybutton = to.getChildByName('game' + this.type, true)
                || to.getChildByName('others', true);
            if (mybutton instanceof ui_1.ButtonContainer) {
                this.zIndex = SceneManager_1.Layer.Top;
                yield this.animateMask(mybutton, true);
            }
            return _super.exitScene.call(this, to);
        });
    }
    enterScene(from, target) {
        const _super = Object.create(null, {
            enterScene: { get: () => super.enterScene }
        });
        return __awaiter(this, void 0, void 0, function* () {
            // Have a cool zoom animation
            if (target instanceof ui_1.ButtonContainer) {
                yield this.animateMask(target);
            }
            return _super.enterScene.call(this, from);
        });
    }
    brushYSpot(index) {
        const c = this.isLandscapeBox ? this.roomContainer.y + (0, Global_1.px)(40) : this.center.y;
        return index ? c : (c - (0, Global_1.px)(12));
    }
    brushXSpot(index) {
        return index ? (index === 1 ? 0 : this.app.screen.width) : this.center.x;
    }
    get triggersStart() {
        return true;
    }
    layout() {
        super.layout();
        if (this.isLandscapeBox) {
            this.roomContainer.position.set(this.center.x, this.center.y - (0, Global_1.px)(15));
        }
        else {
            this.roomContainer.position.set(this.center.x, this.center.y + (0, Global_1.px)(70));
        }
        if (this.isInited) {
            this.footerText.position.y = this.app.screen.height - this.footerText.height + (0, Global_1.px)(15);
        }
        this.footerText.position.x = this.center.x;
        const style = blocks_1.Block.textStyle(this.overSmallSize ? 72 : 52);
        style.align = 'center';
        style.lineHeight = (0, Global_1.px)(this.overSmallSize ? 80 : 60);
        this.text.style = style;
        if (this.isLandscapeBox) {
            this.centerText.position.set(this.center.x, (0, Global_1.px)(10));
        }
        else {
            this.centerText.position.set(this.center.x, this.app.screen.height / 4 - this.centerText.height / 3);
        }
        for (let i = this.brushes.length - 1; i >= 0; i--) {
            const brushSprite = this.brushes[i];
            brushSprite.scale.set((0, Global_1.px)(this.overSmallSize ? 0.1333 : 0.1133));
            const ind = this.brushes.length - 1 - i;
            if (this.playerlist[ind]) {
                brushSprite.position.set(this.isLandscapeBox ? this.center.x / 2 : this.center.x, this.brushYSpot(i));
            }
            else {
                brushSprite.position.set(this.brushXSpot(i), this.brushYSpot(i));
            }
        }
        const fontsize = this.overSmallSize ? 28 : 24;
        const space = this.overSmallSize ? 18 : 12;
        for (let i = 0, counter = 0; i < this.roomContainer.children.length; i += 2, counter++) {
            const y = (t) => counter * (t.height + (0, Global_1.px)(space));
            // Label
            const label = (this.roomContainer.children[i]);
            label.position.y = y(label);
            label.style.fontSize = (0, Global_1.fontSize)(fontsize);
            // Value
            const value = (this.roomContainer.children[i + 1]);
            value.position.y = y(value) + value.height / 2;
            value.style.fontSize = (0, Global_1.fontSize)(fontsize);
        }
    }
}
exports.ActionRoomScene = ActionRoomScene;
